import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Layers, AlertTriangle, Info, Boxes, Check, Scissors, Link2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { conceptName } from "@/lib/breakoutLabels";

const InfoDot = ({ text }) => (
  <span title={text} className="inline-flex cursor-help text-zinc-500 hover:text-zinc-300 transition-colors"><Info size={13} /></span>
);

function Sparkline({ ranks, w = 100, h = 24 }) {
  const pts = (ranks || []).filter((r) => r != null);
  if (pts.length < 2) return <span className="text-xs text-zinc-500">{pts.length ? `#${pts[0]}` : "—"}</span>;
  const max = Math.max(...pts), min = Math.min(...pts), span = max - min || 1, step = w / (pts.length - 1);
  const c = pts.map((r, i) => [i * step, h - ((max - r) / span) * (h - 5) - 2.5]);
  const d = c.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  return <svg width={w} height={h}><path d={d} fill="none" stroke="#a1a1aa" strokeWidth="1.4" strokeLinecap="round" /></svg>;
}

function ConceptGroup({ c, onDone }) {
  const [sel, setSel] = useState({});
  const [newName, setNewName] = useState("");
  const [open, setOpen] = useState(true);
  const selected = Object.keys(sel).filter((k) => sel[k]);

  const resolve = async (decision) => {
    const body = { concept: c.canonical_concept, decision };
    if (decision === "SPLIT" || decision === "RELINK") {
      if (!selected.length) { toast.error("Select the listings to move first"); return; }
      if (!newName.trim()) { toast.error("Enter the more specific product name"); return; }
      body.asins = selected; body.target_concept = newName.trim();
    }
    try {
      await api.post("/amazon/grouping-resolve", body);
      toast.success("Grouping resolved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid={`group-${c.canonical_concept}`} className="bg-[#18181B] border border-[#27272A] rounded-xl overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left p-5 flex items-center justify-between hover:bg-[#1c1c1f]/60 transition-colors">
        <div className="flex items-center gap-2.5">
          {open ? <ChevronDown size={16} className="text-zinc-500" /> : <ChevronRight size={16} className="text-zinc-500" />}
          <span className="text-base font-semibold text-zinc-100 capitalize">{conceptName(c.canonical_concept)}</span>
          <span className="text-xs text-zinc-500">{c.unique_asins} listings · {(c.categories || []).join(", ")}</span>
          <span className="inline-flex items-center gap-1 text-xs text-amber-400 border border-amber-400/20 bg-amber-400/10 rounded-full px-2 py-0.5">
            <AlertTriangle size={11} /> Grouping needs review
          </span>
        </div>
      </button>
      {open && (
        <div className="border-t border-[#27272A] bg-[#09090B]/50 p-5">
          {c.differing_terms?.length > 0 && (
            <div className="text-xs text-zinc-400 mb-4">Distinguishing words seen across listings: <span className="text-amber-300">{c.differing_terms.join(", ")}</span></div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-5">
            {c.asins.map((a) => (
              <label key={a.asin} data-testid={`group-asin-${a.asin}`} className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${sel[a.asin] ? "border-emerald-500/50 bg-emerald-400/5" : "border-[#232326] bg-[#0e0e10] hover:border-zinc-700"}`}>
                <input type="checkbox" data-testid={`group-check-${a.asin}`} checked={!!sel[a.asin]} onChange={(e) => setSel((s) => ({ ...s, [a.asin]: e.target.checked }))} className="mt-1 accent-emerald-500" />
                <div className="w-12 h-12 shrink-0 rounded border border-[#27272A] bg-[#141416] flex items-center justify-center overflow-hidden">
                  {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-contain" /> : <Boxes size={14} className="text-zinc-700" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-zinc-200 leading-snug line-clamp-2">{a.title}</div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-500">
                    <span>#{a.rank_by_date?.length ? a.rank_by_date[a.rank_by_date.length - 1].rank : "—"}</span>
                    <span>first {a.first_seen}</span>
                    {a.variant_attributes?.length > 0 && <span className="text-zinc-600">[{a.variant_attributes.join(" · ")}]</span>}
                    <Sparkline ranks={(a.rank_by_date || []).map((x) => x.rank)} />
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button data-testid={`keep-grouped-${c.canonical_concept}`} onClick={() => resolve("KEEP_GROUPED")} className="inline-flex items-center gap-1.5 text-sm font-medium border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10 rounded-md px-3 py-2 transition-colors"><Check size={14} /> Same product — keep grouped</button>
            <button data-testid={`keep-variants-${c.canonical_concept}`} onClick={() => resolve("KEEP_VARIANTS")} className="inline-flex items-center gap-1.5 text-sm font-medium border border-[#3f3f46] text-zinc-300 hover:bg-zinc-800 rounded-md px-3 py-2 transition-colors"><Check size={14} /> Normal variants — keep grouped</button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2.5 border-t border-[#232326] pt-3">
            <input data-testid={`split-name-${c.canonical_concept}`} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="More specific product name (e.g. self-heating tumbler)" className="bg-[#09090B] border border-[#27272A] rounded-md px-3 py-2 text-sm text-zinc-100 w-72 focus:border-emerald-500/60 outline-none" />
            <button data-testid={`split-${c.canonical_concept}`} onClick={() => resolve("SPLIT")} className="inline-flex items-center gap-1.5 text-sm font-medium border border-amber-400/30 text-amber-300 hover:bg-amber-400/10 rounded-md px-3 py-2 transition-colors"><Scissors size={14} /> Split selected ({selected.length}) into new</button>
            <button data-testid={`relink-${c.canonical_concept}`} onClick={() => resolve("RELINK")} className="inline-flex items-center gap-1.5 text-sm font-medium border border-[#3f3f46] text-zinc-300 hover:bg-zinc-800 rounded-md px-3 py-2 transition-colors"><Link2 size={14} /> Relink selected ({selected.length})</button>
          </div>
          <div className="text-xs text-zinc-500 mt-2">Split only for a materially different <span className="text-zinc-300">function / mechanism / use case</span> — not for color, size, capacity, pack or brand.</div>
        </div>
      )}
    </div>
  );
}

export default function GroupingReview() {
  const [d, setD] = useState(null);
  const load = () => api.get("/amazon/grouping-queue").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-8 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="p-8 max-w-[1200px] mx-auto" data-testid="grouping-review-page">
      <div className="flex items-center gap-2.5 mb-1.5">
        <Layers className="text-amber-400" size={22} />
        <h1 className="font-head text-2xl font-bold tracking-tight text-zinc-50">Grouping review</h1>
      </div>
      <p className="text-sm text-zinc-400 max-w-2xl">
        These product groups may mix different product types. Confirm each is really one idea before we trust its combined trajectory — split only a materially different subtype (function/mechanism/use case), never a color or size.
      </p>
      <div className="text-sm text-zinc-400 mt-6 mb-4" data-testid="grouping-count">
        <span className="text-zinc-100 font-semibold">{d.total}</span> group{d.total === 1 ? "" : "s"} to review
      </div>
      {d.concepts.length === 0 ? (
        <div className="border border-dashed border-[#27272A] rounded-xl p-8 text-center text-sm text-zinc-500" data-testid="grouping-empty">
          Nothing to review — all groupings are confirmed.
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {d.concepts.map((c) => <ConceptGroup key={c.canonical_concept} c={c} onDone={load} />)}
        </div>
      )}
    </div>
  );
}
